import { BrowserRouter as Router, Navigate, useLocation, useNavigate } from 'react-router-dom';
import AppRoutes from './appRoutes';
import InitialPassword from './components/pages/authentication/InitialPassword';
import { customerOnboardingStage } from './services/authentication/customerOnboarding';
import SetupWorkspace from './components/pages/accountSetup/SetupWorkspace';
import DemoBanner from './components/accountSetup/DemoBanner';
import { isImpersonating } from './stores/appImpersonationStore';
import { isDemoSession } from './services/accountSetup/demoSession';
import NavBar from './components/common/NavBar';
import { ImpersonationBanner } from './components/common/ImpersonationControls';
import SideBarMenu from './components/common/SideBarMenu';
import BottomBar from './components/shared/mobile/BottomBar';
import { isPublicAppPath, MainPageRestrictionRoute } from './components/mainPageRestrictionRoute';
import { InvoiceOnlyRedirect } from './components/invoiceOnlyRoute';
import { useAuth } from './services/authentication/authContext';
import { useEffect, useState, useRef } from 'react';
import { useRestaurantStore } from './stores/appRestaurantStore';
import { loadInitialUserDefaults } from './services/userdefaults/initialUserDefaultsService';
import { initEasterEgg } from './utils/easterEgg';
import { useAppUserDefaultStore } from './stores/appUserDefaultStore';
import { supabase } from './services/authentication/supabaseService';
import { useSubscription } from './hooks/useSubscription';
import { trackPageView, identifyUser, resetUser, setRestaurantAnalyticsContext, buildRestaurantAnalyticsContext } from './services/analytics/posthogService';
import { OnboardingRoot } from './components/onboarding/OnboardingRoot';
import { canAccessSupportPage, canAccessImpersonationCustomerPath } from './services/accessManagement/accessManagement';
import { useSupportUnread } from './stores/appSupportStore';
import { SupportNavLink } from './components/common/SupportNavLink';

function AppContent() {
  const { user, backendUser, accountSettings, loading: authLoading, refreshProfile } = useAuth();
  const { isActive } = useSubscription();
  const loadRestaurantData = useRestaurantStore(s => s.loadAllData);
  const restaurants = useRestaurantStore(s => s.restaurants);
  const isRestaurantDataLoaded = useRestaurantStore(s => s.isDataLoaded);
  const isLoadingRestaurantData = useRestaurantStore(s => s.isLoading);
  const isLoadingUserDefaults = useAppUserDefaultStore(s => s.isLoaded);
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const [userInitialized, setUserInitialized] = useState(false);
  const location = useLocation();
  const prevUserIdRef = useRef<string | null>(null);

  const demoEntry = location.pathname === '/demo-entry';
  const publicPath = isPublicAppPath(location.pathname);
  // Setup reviews use shared cost-price routes; keep their setup shell while impersonating too.
  const setupReviewPath = /^\/costprice\/automation-review(?:\/|$)/.test(location.pathname);
  const accountInSetup = !!user && !!backendUser && accountSettings.includes('account_setup') && !isDemoSession()
    && (!isImpersonating() || (location.pathname === '/setup' || location.pathname.startsWith('/setup/')) || setupReviewPath);
  const onboardingStage = customerOnboardingStage(backendUser, isActive, isImpersonating());
  const onboardingBlocked = !!onboardingStage;
  const managedAccountPath = !!backendUser?.managed_customer && (
    ['/payment', '/success'].includes(location.pathname) ||
    (onboardingStage === 'payment' && location.pathname === '/userProfile')
  );
  const setupMode = accountInSetup && !demoEntry && !publicPath && !onboardingBlocked && !managedAccountPath;
  const isRecipeViewer = location.pathname.startsWith('/recipe-viewer/');
  const analyticsRestaurant = isRecipeViewer
    ? restaurants.find(restaurant => restaurant.id === location.pathname.split('/')[2]) ?? null
    : selectedRestaurant;

  const specialPaths = [
    '/forgot-password',
    '/reset-password',
    '/access',
  ];
  let isSpecificEndpoint = false;
  if (typeof window !== 'undefined' && window.location) {
    const pathname = window.location.pathname;
    isSpecificEndpoint = specialPaths.includes(pathname) || pathname.startsWith('/admin');
  }
  const supportEnabled = canAccessSupportPage(backendUser);
  const onSupportPage = location.pathname === '/support' || location.pathname.startsWith('/support/');
  useSupportUnread(!onboardingBlocked && supportEnabled && !isSpecificEndpoint && !accountInSetup && !demoEntry && !authLoading && !!backendUser);
  const showAppNavigation = !onboardingBlocked && user && !!backendUser && !authLoading && !accountInSetup && !demoEntry && !isSpecificEndpoint && !isRecipeViewer && (isActive || (onSupportPage && supportEnabled));

  // ANALYTICS: Keep the event-time restaurant context in sync with the selected
  // restaurant. Registered as PostHog super properties, so it must update BEFORE
  // the pageview effect below fires — hence this effect is declared first (React
  // runs effects in declaration order), guaranteeing a switch re-attributes the
  // next event and never leaks a pageview into the previous selection.
  useEffect(() => {
    const context = buildRestaurantAnalyticsContext(analyticsRestaurant, restaurants);
    if (context) {
      setRestaurantAnalyticsContext(context);
    }
  }, [analyticsRestaurant, restaurants]);

  // ANALYTICS: The restaurant context is ready to attribute a product pageview
  // once (a) there is no authenticated user (pre-auth funnel), (b) we are on an
  // admin/special page that carries no restaurant context, (c) a selection has
  // resolved, or (d) restaurant data loaded but the user has no restaurants.
  // Until then we hold the pageview back so we never emit a falsely unattributed
  // product pageview during startup.
  const analyticsContextReady =
    !user ||
    isSpecificEndpoint ||
    analyticsRestaurant !== null ||
    (isRestaurantDataLoaded && restaurants.length === 0);

  // ANALYTICS: Track page views on route changes
  useEffect(() => {
    if (!analyticsContextReady) return;
    // Viewer queries include dish/allergen searches; do not send them to analytics.
    trackPageView(location.pathname + (isRecipeViewer ? '' : location.search));
  }, [location.pathname, location.search, analyticsContextReady, isRecipeViewer]);

  // ANALYTICS: Identify user when authenticated, reset on logout
  useEffect(() => {
    if (backendUser && backendUser.id !== prevUserIdRef.current) {
      identifyUser(backendUser.id, {
        email: user?.email || undefined,
        role: backendUser.role,
      });
      prevUserIdRef.current = backendUser.id;
    } else if (!user && prevUserIdRef.current) {
      resetUser();
      prevUserIdRef.current = null;
    }
  }, [user, backendUser]);

  // INIT USER DATA - This must complete before backendUser is loaded
  useEffect(() => {
    const initUser = async () => {
      if (!user || userInitialized) return;

      // Handle mock auth mode
      if (process.env.REACT_APP_MOCK_AUTH === 'true') {
        const response = await fetch('/api/users/init-user', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer MOCK_TOKEN'
          }
        });
        if (!response.ok) throw new Error(`User initialization failed: ${response.status}`);
        setUserInitialized(true);
        await refreshProfile();
        return;
      }
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) return;

      const response = await fetch('/api/users/init-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }
      });

      if (!response.ok) throw new Error(`User initialization failed: ${response.status}`);
      setUserInitialized(true);
      // The initial /me request can finish before this account row exists.
      // Reload it after creation so the fail-closed shell can resolve without a focus event.
      await refreshProfile();
    };
    void initUser().catch(error => console.error('Failed to initialize user:', error));
  }, [user, userInitialized, refreshProfile]);

  // INIT RESTAURANT DATA
  useEffect(() => {
    if (onboardingBlocked || accountInSetup || demoEntry || !user || !userInitialized || !backendUser || authLoading || isLoadingRestaurantData || isRestaurantDataLoaded)  {
      return;
    }
    loadRestaurantData();
  }, [onboardingBlocked, accountInSetup, demoEntry, user, userInitialized, backendUser, authLoading, isLoadingRestaurantData, isRestaurantDataLoaded, loadRestaurantData]);

  // INIT USER DEFAULTS
  useEffect(() => {
    if (onboardingBlocked || accountInSetup || demoEntry || !isRestaurantDataLoaded || isLoadingUserDefaults || !backendUser || !restaurants.length) {
      return;
    }

    loadInitialUserDefaults(restaurants, backendUser.id);
  }, [onboardingBlocked, accountInSetup, demoEntry, isRestaurantDataLoaded, isLoadingUserDefaults, backendUser, restaurants]);

  // Easter egg for specific emails
  useEffect(() => {
    if (!user?.email) return;

    try {
      const allowedEmails = JSON.parse(process.env.REACT_APP_EASTEREGG_EMAILS || '[]');
      if (allowedEmails.includes(user.email)) {
        initEasterEgg();
      }
    } catch (e) {
      console.error('Failed to parse REACT_APP_EASTEREGG_EMAILS', e);
    }
  }, [user?.email]);

  const navigate = useNavigate();

  // Check if user is staff with pending or rejected access
  const isPendingOrRejectedStaff = backendUser?.role === 'staff' &&
    (backendUser.access_request === 'pending' || backendUser.access_request === 'rejected');

  // Redirect to /access for staff with pending/rejected status
  useEffect(() => {
    if (user && backendUser && isPendingOrRejectedStaff && location.pathname !== '/access') {
      navigate('/access', { replace: true });
    }
  }, [user, backendUser, isPendingOrRejectedStaff, location.pathname, navigate]);

  // Hold all operational pages on a blank screen until the account-mode response has resolved,
  // so the first real render is either the setup workspace or the normal app — never an error flash.
  if (!publicPath && user && (authLoading || !backendUser)) return <div className="min-h-screen bg-backgroundDarker dark:bg-backgroundDarker-dark" role="status" aria-busy="true" />;
  if (user && backendUser && onboardingStage === 'password' && (!publicPath || location.pathname === '/login')) return <InitialPassword />;
  if (user && backendUser && onboardingStage === 'payment' && !publicPath && !managedAccountPath) return <Navigate to="/payment" replace />;
  if (location.pathname === '/initial-password' && user && backendUser) return <Navigate to={isActive ? (accountInSetup ? '/setup' : '/dashboard') : '/payment'} replace />;
  if (setupMode) return <><ImpersonationBanner /><SetupWorkspace><AppRoutes /></SetupWorkspace></>;
  if (!canAccessImpersonationCustomerPath(backendUser, accountSettings, location.pathname)) return <Navigate to="/dashboard" replace />;

  return (
    <div className="h-screen flex flex-col">
      {/* Impersonation banner — rendered at root so a super admin can always exit,
          even on subscription-gated pages (e.g. an unpaid account's payment page). */}
      <ImpersonationBanner />
      <DemoBanner />

      {/* Fixed navbar at top with 80px height — desktop only; below lg every page
          renders its own MobilePageHeader and the BottomBar carries navigation. */}
      {showAppNavigation && <div className="hidden lg:block"><NavBar /></div>}

      {/* Container for sidebar and main content */}
      <div className="flex flex-1 overflow-hidden">
        {showAppNavigation && <div className="hidden lg:block"><SideBarMenu /></div>}

        <main id="mainContainer" className={isRecipeViewer ? "flex-1 min-h-0 overflow-hidden" : "flex-1 overflow-y-auto bg-backgroundDarker dark:bg-backgroundDarker-dark pb-28 lg:pb-0"}>
          <div className={isRecipeViewer ? "h-full" : undefined}>
            {/* Invoice-only accounts are confined to their allowlist; anything else
                (deep link, bookmark, the /dashboard login sends everyone to) → /home. */}
            {demoEntry ? <AppRoutes /> : (
              <MainPageRestrictionRoute>
                <InvoiceOnlyRedirect>
                  <AppRoutes />
                </InvoiceOnlyRedirect>
              </MainPageRestrictionRoute>
            )}
          </div>
        </main>
      </div>

      {/* Mobile bottom nav — replaces the sidebar below lg. Same guard as NavBar/SideBarMenu. */}
      {showAppNavigation && <BottomBar />}
      {!onboardingBlocked && user && !accountInSetup && !isActive && !isSpecificEndpoint && !isRecipeViewer && !onSupportPage && supportEnabled && (
        <div className="fixed bottom-5 right-5 z-40 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
          <SupportNavLink className="flex items-center gap-2 p-3 text-emerald-700 dark:text-emerald-300" />
        </div>
      )}
      {!onboardingBlocked && !isRecipeViewer && !accountInSetup && !demoEntry && <OnboardingRoot />}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
